import { captureException } from '@sentry/react'
import { useQueryClient } from '@tanstack/react-query'
import { useState, useRef, useMemo } from 'react'

import type { IContext } from './columns'
import type { FiltersState } from '../Filters'
import type { ReactElement } from 'react'
import type { IReplyReviewMacro } from '~/types/review'

import columns from './columns'

import ManageReplyMacroModal from '../../Modals/ManageReplyMacroModal'
import ReviewsManagementFilters, { applicationFilterFn } from '../Filters'

import ContentLayout from '~/components/ContentLayout'
import Layout from '~/components/Layouts/Layout'
import ModalDialog from '~/components/ModalDialog'
import TableWrapper from '~/components/Tables/TableWrapper'
import ViewTitle from '~/components/ViewTitle'
import { AUTHORIZATIONS } from '~/const'
import { FASTLY_PATH } from '~/const/asset'
import useDocumentTitle from '~/hooks/useDocumentTitle'
import { useIsUserAuthorized } from '~/providers/Authorizations'
import { useAppsManagementListQuery } from '~/queries/application-management'
import {
  keys as replyMacroKeys,
  useDeleteReviewReplyMacroMutation,
  useReviewsReplyMacrosQuery,
} from '~/queries/reviews-reply-macros'
import { resize } from '~/utils/media'
import { displayToast } from '~/utils/toast'

import './styles.scss'

const ENTRIES_NAME = 'reviews-reply-macro'

const ReplyMacros = (): ReactElement => {
  /** Local state */

  const queryClient = useQueryClient()

  const tableRef = useRef<HTMLDivElement>(null)

  const [isManageModalOpen, setIsManageModalOpen] = useState(false)

  const [isDeleteModalOpen, setDeleteModalState] = useState(false)

  const [selectedMacro, setSelectedMacro] = useState<IReplyReviewMacro>()

  const [filters, setFilters] = useState<FiltersState>()

  const reviewsWriteAuthorized = useIsUserAuthorized([AUTHORIZATIONS.REVIEWS.WRITE])

  /** Queries & Mutations */

  const { data: apps } = useAppsManagementListQuery()

  const { data: macros } = useReviewsReplyMacrosQuery()

  const { mutateAsync: deleteMacro } = useDeleteReviewReplyMacroMutation()

  /** Effects */

  useDocumentTitle('Reviews | Reply macros')

  /** Handlers */

  const handleFiltersChange = (newFilters: FiltersState) => setFilters(newFilters)

  const handleManageModalState = (isOpen: boolean, macro?: IReplyReviewMacro) => () => {
    setSelectedMacro(macro)
    setIsManageModalOpen(isOpen)
  }

  const handleToggleDeleteModal = (isOpen: boolean, macro?: IReplyReviewMacro) => () => {
    setSelectedMacro(macro)
    setDeleteModalState(isOpen)
  }

  const handleDeleteModalConfirm = async () => {
    if (selectedMacro) {
      try {
        await deleteMacro({ macroId: selectedMacro.id })
        await queryClient.invalidateQueries({ queryKey: replyMacroKeys.list() })

        displayToast('Macro deleted', 'success')
      } catch (e) {
        displayToast('Macro could not be deleted', 'error')
        captureException(e)
      } finally {
        setDeleteModalState(false)
      }
    }
  }

  const handleManageMacroSuccess = () => {
    setIsManageModalOpen(false)
    displayToast(`Macro ${selectedMacro ? 'edited' : 'created'}`, 'success')
  }

  const handleManageMacroFailure = () => {
    setIsManageModalOpen(false)
    displayToast(`Macro could not be ${selectedMacro ? 'edited' : 'created'}`, 'error')
  }

  const rowData = useMemo(
    () =>
      macros?.filter(applicationFilterFn(filters)).map((macro) => {
        return {
          ...macro,
          applications: macro.applications?.map((app) => {
            const application = apps?.find(({ app_key }) => app_key === app.app_key)
            return {
              ...app,
              app_icon_url: application?.icon_file_id
                ? resize(`${FASTLY_PATH}${application.icon_file_id}`, 32, 32)
                : undefined,
            }
          }),
        }
      }),
    [apps, filters, macros]
  )

  const context = useMemo(
    (): IContext => ({
      handleDelete: handleToggleDeleteModal,
      handleEdit: handleManageModalState,
      reviewsWriteAuthorized: reviewsWriteAuthorized,
    }),
    [reviewsWriteAuthorized]
  )

  /** Render */

  return (
    <>
      <ViewTitle className="layout-view-title top-zero">Reply macros management</ViewTitle>
      <ReviewsManagementFilters onFiltersChange={handleFiltersChange} />
      <Layout className="reviews-reply-macro-view" withPadding>
        <ContentLayout>
          <TableWrapper
            addButton
            addButtonDisabled={!reviewsWriteAuthorized}
            addButtonLabel="New macro"
            addButtonTooltip={!reviewsWriteAuthorized ? 'unauthorized' : undefined}
            className="reviews-reply-macro-listing-table with-min-height"
            columnDefs={columns}
            context={context}
            entriesName={ENTRIES_NAME}
            onAddButtonClick={handleManageModalState(true)}
            rowData={rowData}
            rowStyle={{ cursor: 'default' }}
            suppressScrollOnNewData
            tableRef={tableRef}
            withPagination={false}
            withSearchBar
          />
        </ContentLayout>
      </Layout>
      <ModalDialog
        isOpen={isDeleteModalOpen}
        mainText="Do you really want to delete this macro?"
        onClose={handleToggleDeleteModal(false)}
        onConfirm={handleDeleteModalConfirm}
      />
      <ManageReplyMacroModal
        isOpen={isManageModalOpen}
        macro={selectedMacro}
        onClose={handleManageModalState(false)}
        onFailure={handleManageMacroFailure}
        onSuccess={handleManageMacroSuccess}
      />
    </>
  )
}

export default ReplyMacros
